import * as React from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@antimatterhq/ui/lib/utils';
import { InfoTooltip } from '@antimatterhq/ui/components/infoTooltip';

const labelVariants = cva(
  'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
  {
    variants: {
      variant: {
        default: 'text-grey-800 dark:text-grey-100',
        success: 'text-green-600 dark:text-green-500',
        warning: 'text-yellow-600 dark:text-yellow-500',
        error: 'text-red-600 dark:text-red-500',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

type LabelProps = {
  mb?: number | string;
  required?: boolean;
  info?: string;
  size?: Size;
} & (
  | { bold?: false; semiBold?: false }
  | { bold: true; semiBold?: false }
  | { bold?: false; semiBold: true }
) &
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> &
  VariantProps<typeof labelVariants>;

export const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  LabelProps
>(
  (
    {
      className,
      variant,
      mb = 1,
      required,
      info,
      size = 'sm',
      bold,
      semiBold,
      children,
      ...props
    },
    ref,
  ) => (
    <div className={cn(mb && `mb-${mb}`, 'flex items-center gap-2')}>
      <LabelPrimitive.Root
        ref={ref}
        className={cn(
          labelVariants({ variant }),
          className,
          size === 'sm' && 'text-sm',
          size === 'base' && 'text-base',
          size === 'lg' && 'text-lg',
          size === 'xl' && 'text-xl',
          size === '2xl' && 'text-2xl',
          size === '3xl' && 'text-3xl',
          bold && 'font-bold',
          semiBold && 'font-semibold',
        )}
        {...props}
      >
        {children}
        {required && (
          <span className="ml-1 text-red-600 dark:text-red-500">*</span>
        )}
      </LabelPrimitive.Root>
      {!!info && <InfoTooltip content={info} />}
    </div>
  ),
);

Label.displayName = LabelPrimitive.Root.displayName;
